import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, Redirect } from "react-router";
import NavigationLink from "../../../components/NavigationLink/NavigationLink"; // Adjust the path as needed
import { Filter } from "../../../components/Table/Migration/LegacyTypes";
import { Popup, Loader } from "semantic-ui-react";
import {
  editedObjectIdSelector,
  isEditActiveSelector,
  editedObjectIdentifierSelector
} from "../../../reducers/editReducer";
import ExcelExport from "../../../components/ExcelExport/ExcelExport";
import GlobalTableSettings from "../../GlobalTableSettings/GlobalTableSettings";
import ActionMenuButton from "../../../components/ActionMenuButton/ActionMenuButton";
import { parseNameToUrl, parseUrlToName, hasWriteAccess } from "../../../utils/utils";
import exportIcon from "../../../images/export.svg";
import add from "../../../images/circle-plus.svg";
import styles from "../../../components/ActionMenuButton/ActionMenuButton.module.scss";
import { editActions } from "../../../actions/editActions";
import service from "../../../images/service-24.png";
import { createEmptyDoorVersion, setDoorVersionExtraProperties } from "../../../utils/pdApiUtils";
import classNames from "classnames";
import DoorVersion from "./DoorVersion";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal"; // Adjust the path as needed
import ModifyDoorParameterBindings from "../../../components/ModifyDoorParameterBindings/ModifyDoorParameterBindings";

import { Column } from "../../../components/Table/Migration/LegacyTypes";
import { getIndexedValue, searchMatchRule } from "../../../utils/utils";

// Utility function to filter rows based on user selection
function filterWithUserSelection(
  userFiltering: Filter[] | undefined,
   // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows: any[],
  columns: State.ColumnMap,
  activeMenu: State.DoorBindingsSubmenu
   // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any[] {
  if (!userFiltering || userFiltering.length === 0) {
    return rows;
  }
  return rows.filter(row =>
    userFiltering.every(filter => {
      const column: State.Column | undefined = columns[activeMenu as keyof State.ColumnMap].find(
        (col: Column) => col.id === filter.id
      );
      const propertyPath = column?.ownAccessorName || "";
      const propertyPathArray = propertyPath.split(".").slice(1);
      const rowValue = getIndexedValue(propertyPathArray, row);
      const included =
        filter.value === "all"
          ? true
          : !!rowValue && !!filter.value
          ? column?.hasDropdownFiltering
            ? rowValue.toString() === filter.value.toString()
            : searchMatchRule(rowValue.toString(), filter.value.toString())
          : false;
      return included;
    })
  );
}

interface DoorBindingsProps {
  subMenuRoute: string;
  children?: React.ReactNode;
}

const DoorBindings: React.FC<DoorBindingsProps> = () => {
  const dispatch = useDispatch();
  const subMenus: State.DoorBindingsSubmenu[] = [
    "Add Bindings",
    "Remove Bindings",
    "Door Versions"
  ];
  // State initialization
  const { activeMenu: activeMenuParam } = useParams<{ activeMenu?: string }>();
  const initialActiveMenu: State.DoorBindingsSubmenu = activeMenuParam
    ? (parseUrlToName(activeMenuParam) as State.DoorBindingsSubmenu)
    : "Door Versions";

  const [activeMenu, setActiveMenu] = useState<State.DoorBindingsSubmenu>(initialActiveMenu);

  const [isModalOpen, setIsModalOpen] = useState(false);
  // eslint-disable-next-line
  const [tableColumnFilters, setTableColumnFilters] = useState<
    Record<State.DoorBindingsSubmenu, Filter[]>
  >({
    "Add Bindings": [],
    "Remove Bindings": [],
    "Door Versions": []
  });

  const pdApi = useSelector((state: State.Root) => state.pdApi);
  const columns = useSelector((state: State.Root) => state.columns);
  const mainState = useSelector((state: State.Root) => state.main);
  const isEditActive = useSelector((state: State.Root) => isEditActiveSelector(state.editView));
  const editedObjectId = useSelector((state: State.Root) => editedObjectIdSelector(state.editView));
  const objectIdentifier = useSelector((state: State.Root) =>
    editedObjectIdentifierSelector(state.editView, initialActiveMenu)
  );
  const loadingStatus = useSelector((state: State.Root) => state.loadingStatus.isLoadingParameters);

  const { activeUser } = mainState;
  const userPermissions = hasWriteAccess(activeUser, "Door");

  useEffect(() => {
    const parseActiveMenuFromURL = initialActiveMenu
      ? (parseUrlToName(initialActiveMenu) as State.DoorBindingsSubmenu)
      : "Add Bindings";

    if (activeMenu !== parseActiveMenuFromURL) {
      setActiveMenu(parseActiveMenuFromURL);
    }
  }, [initialActiveMenu, activeMenu]);

  const updateColumnFilters = useCallback((subMenuRoute: string, newFilters: Filter[]) => {
    setTableColumnFilters(prevFilters => ({
      ...prevFilters,
      [subMenuRoute]: newFilters
    }));
  }, []);

  // Toggle modal
  const toggleModal = useCallback(() => {
    setIsModalOpen(prev => !prev);
  }, []);

  if (activeMenuParam === undefined) {
    return <Redirect to="/door/bindings/add-bindings" />;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let rows: any = pdApi[activeMenu.split(" ").join("") as keyof State.PdApi] || [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows = rows.map((row: any) => setDoorVersionExtraProperties(row as Api.DoorVersion, pdApi));

  const newItem = createEmptyDoorVersion();
  const filename = "Door Versions";
  const option = "DoorVersions";

  const filteredRows = filterWithUserSelection(tableColumnFilters[activeMenu], rows, columns, activeMenu);

  return (
    <>
      <div className="table-container">
        <div className="bindingsWrapper">
          <div className="bindingsTabs">
            {subMenus.map(menu => (
              <NavigationLink
                key={menu}
                to={"/door/bindings/" + parseNameToUrl(menu)}
                name={menu}
              />
            ))}
          </div>
          {activeMenu === "Door Versions" && (
            <div className="actionsMenu">
              <div className="bindingActions">
                {filteredRows.length > 0 && hasWriteAccess(activeUser, "Door") ? (
                  <ActionMenuButton>
                    <div className="actionBtn">
                      <img src={exportIcon} alt="Export to excel" />
                      <ExcelExport
                        rows={filteredRows}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        columns={columns[activeMenu as keyof typeof columns]}
                        filename={filename}
                      />
                    </div>
                  </ActionMenuButton>
                ) : (
                  <ActionMenuButton>
                    <div className="actionBtn">
                      <img
                        className={`${styles.icon} ${styles.disableImg}`}
                        src={exportIcon}
                        alt="Export to excel"
                      />
                      <span className={styles.disableText} style={{ textTransform: "capitalize" }}>
                        Export
                      </span>
                    </div>
                  </ActionMenuButton>
                )}

                {hasWriteAccess(activeUser, "Door") && !isEditActive ? (
                  <ActionMenuButton
                    id="Add"
                    onClick={() => dispatch(editActions.startEditing(newItem, true))}
                  >
                    <div className="actionBtn">
                      <img src={add} alt="Add new item" />
                      <span style={{ textTransform: "capitalize" }}>Add {activeMenu}</span>
                    </div>
                  </ActionMenuButton>
                ) : (
                  <ActionMenuButton id="Add">
                    <div className="actionBtn">
                      <img src={add} className={styles.disableImg} alt="Add new item" />
                      <span style={{ textTransform: "capitalize" }} className={styles.disableText}>
                        Add {activeMenu}
                      </span>
                    </div>
                  </ActionMenuButton>
                )}
              </div>
              {
                <Popup
                  trigger={
                    <div className="tableSetting">
                      <ActionMenuButton>
                        <div className="actionBtn">
                          <img src={service} alt="Table settings" />
                          <span>Table Settings</span>
                        </div>
                      </ActionMenuButton>
                    </div>
                  }
                  wide="very"
                  content={
                    <div onClick={(e) => e.stopPropagation()}>
                      <GlobalTableSettings />
                    </div>
                  }
                  on="click"
                  position="bottom right"
                  className="tableSettingspopup"
                />
              }
            </div>
          )}
        </div>
        <div
          className={classNames({
            bindingsTabPanels: activeMenu === "Add Bindings" || activeMenu === "Remove Bindings",
            bindingsTabTable: activeMenu === "Door Versions"
          })}
        >
          {activeMenu !== "Door Versions" && loadingStatus ? (
            <Loader inline="centered" content="Loading parameters" active={true} />
          ) : activeMenu === "Add Bindings" ? (
            <ModifyDoorParameterBindings mode="Add" permission={!userPermissions} />
          ) : activeMenu === "Remove Bindings" ? (
            <ModifyDoorParameterBindings mode="Remove" permission={!userPermissions} />
          ) : activeMenu === "Door Versions" ? (
            <DoorVersion
              columns={columns}
              rows={rows}
              mainState={mainState}
              editedObjectId={editedObjectId}
              subMenuRoute={activeMenu}
              toggleModal={toggleModal}
              setColumnFilters={updateColumnFilters}
              isEditActive={isEditActive}
              editActions={editActions}
            />
          ) : null}
        </div>
        {isEditActive && (
          <ConfirmationModal
            isOpen={isModalOpen}
            size="mini"
            header="Confirm delete"
            content={`Are you sure want to delete ${option} ${objectIdentifier}`}
            confirmText="Confirm"
            cancelText="Cancel"
            onConfirm={() => dispatch(editActions.delete(option))}
            onCancel={toggleModal}
          />
        )}
      </div>
    </>
  );
};

export default DoorBindings;
